import React, { useState } from "react";
import {
  Bike, Wallet, ShoppingBag, Truck, Wrench, FileText, Inbox,
  Plus, Save, Trash2, Search, ArrowRight, Gauge, Edit3, ClipboardList,
} from "lucide-react";
import {
  StatCard, DepartmentCard, TaskCard, ProgressStepper,
  StatusBadge, SLABadge, TimelineCard, CustomerSummaryCard,
  SearchFilterBar, UploadCard, EmptyState, ActionButton,
  cardCls, labelCls,
} from "../components/design-system";

export default function DesignPreview() {
  const [activeChip, setActiveChip] = useState("submitted");
  const [filterFiles, setFilterFiles] = useState([
    { name: "Aadhar_Card.pdf", type: "PDF", size: "412 KB", status: "success" },
    { name: "Tax_Invoice_2026.pdf", type: "PDF", size: "1.2 MB", status: "success" },
    { name: "Insurance_Policy.pdf", type: "PDF", size: "880 KB", status: "uploading" },
    { name: "Vehicle_Photo.heic", type: "HEIC", size: "8.4 MB", status: "error", message: "Exceeds 10 MB" },
  ]);

  return (
    <div className="min-h-screen bg-onedesk-bg" data-testid="design-preview-page">
      {/* ── Page Header ───────────────────────── */}
      <header className="border-b border-onedesk-border bg-onedesk-card">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col gap-3">
          <span className={labelCls}>Internal · Preview</span>
          <h1 className="font-bold text-3xl text-onedesk-textPrimary tracking-tight">
            OneDesk 360 — Global Design System
          </h1>
          <p className="font-medium text-base text-onedesk-textSecondary max-w-3xl leading-relaxed">
            12 reusable components built from a fixed token palette (10 colours,
            3 radii, 5-step spacing, single subtle shadow). Approve this preview
            and the same components will be rolled out across every module.
          </p>
          <TokenLegend />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8 flex flex-col gap-8">
        {/* 1. Stat Card */}
        <Section
          n="1"
          title="Stat Card"
          caption="KPI tile · used on Dashboard & manager widgets."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              label="Sales This Month"
              value="142"
              trend="up"
              trendValue="+12%"
              helper="vs last month"
              Icon={Bike}
            />
            <StatCard
              label="Pending Deliveries"
              value="38"
              trend="down"
              trendValue="-5%"
              helper="vs last month"
              Icon={Truck}
            />
            <StatCard
              label="On-Hold Tasks"
              value="6"
              trend="neutral"
              trendValue="0%"
              helper="no change"
              Icon={ClipboardList}
            />
          </div>
        </Section>

        {/* 2. Department Card */}
        <Section
          n="2"
          title="Department Card"
          caption="Module entry tile · used on Admin home & landing."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <DepartmentCard
              title="Accounts"
              description="Tax Invoice, payment receipts and ledger reconciliation."
              count={14}
              Icon={Wallet}
              status="active"
            />
            <DepartmentCard
              title="Back Office"
              description="Insurance, Road Tax, HSRP and VAS service tasks."
              count={27}
              Icon={FileText}
              status="active"
            />
            <DepartmentCard
              title="Spares"
              description="Accessory fulfilment is currently paused at this branch."
              count={0}
              Icon={Wrench}
              status="inactive"
            />
          </div>
        </Section>

        {/* 3. Task Card */}
        <Section
          n="3"
          title="Task Card"
          caption="Queue item · Accounts, PDI, Back Office, Spares."
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TaskCard
              title="Tax Invoice · Accounts"
              saleNumber="SR-2026-0142"
              customer="Anita Sharma · MT-15"
              assignee="Demo Accounts"
              dueLabel="18/May/26, 11:00 AM"
              slaStatus="on_track"
              slaRemaining="45 min left"
              taskStatus="in_progress"
            />
            <TaskCard
              title="Road Tax Submission"
              saleNumber="SR-2026-0138"
              customer="Ravi Kumar · FZ-X"
              assignee="Demo Back Office"
              dueLabel="17/May/26, 06:30 PM"
              slaStatus="delayed"
              slaRemaining="2h 14m over"
              taskStatus="in_progress"
            />
            <TaskCard
              title="HSRP Ordering"
              saleNumber="SR-2026-0131"
              customer="Pooja Singh · R15M"
              assignee="—"
              dueLabel="—"
              slaStatus="on_hold"
              slaRemaining="paused"
              taskStatus="on_hold"
              onHold
              holdReason="RTO Portal Down"
            />
            <TaskCard
              title="VIN Assignment"
              saleNumber="SR-2026-0157"
              customer="Suresh Patel · MT-09"
              assignee="Demo PDI"
              dueLabel="19/May/26, 10:30 AM"
              slaStatus="at_risk"
              slaRemaining="9 min left"
              taskStatus="in_progress"
            />
          </div>
        </Section>

        {/* 4. Progress Stepper */}
        <Section
          n="4"
          title="Progress Stepper"
          caption="Sales Form 10-step flow + workflow stages."
        >
          <div className={`${cardCls} p-6 flex flex-col gap-8`}>
            <div className="flex flex-col gap-3">
              <span className={labelCls}>Workflow · 3 stages</span>
              <ProgressStepper
                steps={[
                  { id: 1, label: "Sale Released" },
                  { id: 2, label: "Workflow In Progress" },
                  { id: 3, label: "Ready for Delivery" },
                ]}
                currentStep={2}
              />
            </div>
            <div className="flex flex-col gap-3">
              <span className={labelCls}>Sales Form · 10 steps</span>
              <ProgressStepper
                currentStep={4}
                steps={[
                  { id: 1, label: "Customer" }, { id: 2, label: "Vehicle" },
                  { id: 3, label: "Finance" }, { id: 4, label: "VAS" },
                  { id: 5, label: "Accessories" }, { id: 6, label: "Insurance" },
                  { id: 7, label: "Road Tax" }, { id: 8, label: "Delivery" },
                  { id: 9, label: "Documents" }, { id: 10, label: "Receipts" },
                ]}
              />
            </div>
          </div>
        </Section>

        {/* 5. Status Badge */}
        <Section
          n="5"
          title="Status Badge"
          caption="Sale & task lifecycle states."
        >
          <div className={`${cardCls} p-6 flex flex-wrap gap-3`}>
            <StatusBadge status="draft" />
            <StatusBadge status="submitted" />
            <StatusBadge status="in_progress" />
            <StatusBadge status="ready" />
            <StatusBadge status="delivered" />
            <StatusBadge status="on_hold" />
          </div>
        </Section>

        {/* 6. SLA Badge */}
        <Section
          n="6"
          title="SLA Badge"
          caption="Computed from sla.compute_task_sla — 5 states."
        >
          <div className={`${cardCls} p-6 flex flex-wrap gap-3`}>
            <SLABadge status="on_track" remaining="42 min" />
            <SLABadge status="at_risk" remaining="9 min" />
            <SLABadge status="delayed" remaining="2h 14m over" />
            <SLABadge status="on_hold" remaining="transit hold" />
            <SLABadge status="paused" remaining="manual pause" />
          </div>
        </Section>

        {/* 7. Timeline Card */}
        <Section
          n="7"
          title="Timeline Card"
          caption="Activity log entries · Sale Detail · Audit Trail."
        >
          <div className="flex flex-col gap-3">
            <TimelineCard
              actor="Demo DSE"
              action="created the sale"
              department="Sales"
              timestamp="18/May/26 · 10:02 AM"
              Icon={Plus}
              variant="action"
            />
            <TimelineCard
              actor="Demo Accounts"
              action="changed Tax Invoice number"
              before="TI-99-1245"
              after="TI-99-1252"
              department="Accounts"
              timestamp="18/May/26 · 11:18 AM"
              Icon={Edit3}
              variant="change"
            />
            <TimelineCard
              actor="Demo Back Office"
              action="added a note"
              note="Road Tax Submitted to RTO. Acknowledgement copy attached. Awaiting receipt of payment confirmation from RTO before HSRP order."
              department="Back Office"
              timestamp="18/May/26 · 02:10 PM"
              Icon={FileText}
              variant="note"
            />
          </div>
        </Section>

        {/* 8. Customer Summary Card */}
        <Section
          n="8"
          title="Customer Summary Card"
          caption="Compact identity panel · Sale Detail · Workspaces."
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CustomerSummaryCard
              name="Anita Sharma"
              mobile="+91 9000 12 3456"
              vehicle="Yamaha MT-15 · Cyan Storm"
              srNumber="SR-2026-0142"
              location="JEY · Jeypore"
            />
            <CustomerSummaryCard
              name="Walk-in Enquiry"
              mobile="+91 9000 99 0000"
            />
          </div>
        </Section>

        {/* 9. Search Filter Bar */}
        <Section
          n="9"
          title="Search Filter Bar"
          caption="Unified row on every queue · search + filters + export."
        >
          <SearchFilterBar
            searchValue=""
            onSearchChange={() => {}}
            placeholder="Search by customer, SR number, mobile…"
            locationLabel="JEY, NAB +2"
            dateLabel="Last 7 days"
            statusChips={[
              { key: "draft", label: "Draft" },
              { key: "submitted", label: "Submitted", active: activeChip === "submitted" },
              { key: "in_progress", label: "In Progress" },
              { key: "delivered", label: "Delivered" },
            ].map((c) => ({ ...c, onClick: () => setActiveChip(c.key) }))}
            activeFilters={[
              { key: "loc", label: "Location · JEY, NAB", onRemove: () => {} },
              { key: "date", label: "Last 7 days", onRemove: () => {} },
              { key: "sla", label: "SLA · Delayed", onRemove: () => {} },
            ]}
            onExport={() => {}}
          />
        </Section>

        {/* 10. Upload Card */}
        <Section
          n="10"
          title="Upload Card"
          caption="Drag-drop with per-file state — used in Sales Form, Workspaces, Delivery File."
        >
          <UploadCard
            files={filterFiles}
            onFilesPicked={() => {}}
            onRemove={(i) => setFilterFiles(filterFiles.filter((_, x) => x !== i))}
          />
        </Section>

        {/* 11. Empty State */}
        <Section
          n="11"
          title="Empty State"
          caption="Used when a queue, list, or detail returns zero items."
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <EmptyState
              Icon={Inbox}
              title="No tasks in your queue"
              description="New tasks will appear here as sales move through the workflow."
              variant="no-data"
            />
            <EmptyState
              Icon={Search}
              title="No matching results"
              description="Adjust your search or clear filters to see more items."
              variant="no-results"
              action={
                <ActionButton variant="secondary" size="sm">
                  Clear Filters
                </ActionButton>
              }
            />
            <EmptyState
              Icon={Gauge}
              title="3 SLA breaches need review"
              description="Open Tasks → SLA · Delayed to investigate and escalate."
              variant="action-required"
              action={
                <ActionButton variant="primary" size="sm" Icon={ArrowRight}>
                  Review Now
                </ActionButton>
              }
            />
          </div>
        </Section>

        {/* 12. Action Buttons */}
        <Section
          n="12"
          title="Action Buttons"
          caption="Four variants · three sizes · with icon / loading / disabled."
        >
          <div className={`${cardCls} p-6 flex flex-col gap-6`}>
            <div className="flex flex-col gap-3">
              <span className={labelCls}>Variants</span>
              <div className="flex flex-wrap items-center gap-3">
                <ActionButton variant="primary">Primary</ActionButton>
                <ActionButton variant="secondary">Secondary</ActionButton>
                <ActionButton variant="destructive">Destructive</ActionButton>
                <ActionButton variant="ghost">Ghost</ActionButton>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <span className={labelCls}>With Icon</span>
              <div className="flex flex-wrap items-center gap-3">
                <ActionButton variant="primary" Icon={Plus}>New Sale</ActionButton>
                <ActionButton variant="secondary" Icon={Save}>Save Draft</ActionButton>
                <ActionButton variant="destructive" Icon={Trash2}>Delete Sale</ActionButton>
                <ActionButton variant="ghost" Icon={ArrowRight}>View Detail</ActionButton>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <span className={labelCls}>States</span>
              <div className="flex flex-wrap items-center gap-3">
                <ActionButton variant="primary" loading>Saving</ActionButton>
                <ActionButton variant="primary" disabled>Disabled</ActionButton>
                <ActionButton variant="secondary" size="sm">Small</ActionButton>
                <ActionButton variant="secondary" size="md">Medium</ActionButton>
                <ActionButton variant="secondary" size="lg">Large</ActionButton>
              </div>
            </div>
          </div>
        </Section>

        <footer className="mt-4 mb-8 text-center">
          <span className={labelCls}>End of preview · awaiting approval</span>
        </footer>
      </main>
    </div>
  );
}

function Section({ n, title, caption, children }) {
  return (
    <section
      className="flex flex-col gap-4"
      data-testid={`section-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-baseline gap-3">
        <span className="font-bold text-onedesk-textSecondary text-sm tabular-nums">
          {n.padStart(2, "0")}
        </span>
        <div className="flex flex-col">
          <h2 className="font-bold text-xl text-onedesk-textPrimary">
            {title}
          </h2>
          <p className="font-medium text-sm text-onedesk-textSecondary">
            {caption}
          </p>
        </div>
      </div>
      <div>{children}</div>
    </section>
  );
}

function TokenLegend() {
  const swatches = [
    { name: "primary", hex: "#0F172A" },
    { name: "secondary", hex: "#64748B" },
    { name: "success", hex: "#10B981" },
    { name: "warning", hex: "#F59E0B" },
    { name: "danger", hex: "#EF4444" },
    { name: "background", hex: "#F8FAFC", border: true },
    { name: "card", hex: "#FFFFFF", border: true },
    { name: "border", hex: "#E2E8F0" },
  ];
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="token-legend">
      {swatches.map((s) => (
        <span
          key={s.name}
          className="inline-flex items-center gap-2 rounded-od-input border border-onedesk-border bg-onedesk-card px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-onedesk-textSecondary"
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded ${s.border ? "border border-onedesk-border" : ""}`}
            style={{ backgroundColor: s.hex }}
          />
          {s.name}
          <span className="font-mono normal-case tracking-normal text-onedesk-textPrimary">
            {s.hex}
          </span>
        </span>
      ))}
    </div>
  );
}
